import { fetchWithAuthToken } from '../../axios';
import { getStyle, getLayerById,makeLayer,removeLayer,setLayerVisible as setLayerVisibleInMap } from '../../js/layers';
import { getMap, getMapAsync } from '../../js/map';
import { geojsonFormat } from '../../js/tools/tooltip';
import { showError } from '../../js/ui-helpers';

const state = () => ({
    layersInitialized: false,
    municipalityLayerEnabled: false,
    guiLayersInfo: [],
});

const getters = {
    layersInitialized: state => state.layersInitialized,
    getGUILayersInfo: state => state.guiLayersInfo,
    getPlanLayerIds: () =>
    {
        return getInitialPlanLayerInfos().layers.map(layer=>layer.id);
    },
};

const actions = {
    async fetchLayers({commit},)
    {
        try
        {
            const response = await fetchWithAuthToken('api/layers');
            const data = [ getInitialPlanLayerInfos(),...response.data ];
            commit('SET_GUI_LAYERS_INFO', data.filter(ctgry => ctgry.layers.length));
            const layerInfos = data.map(ctgry => ctgry.layers).reduce((accumulated, currVal) => [...accumulated,...currVal], []);
            const layersToAdd = layerInfos.filter(layerInfo => !layerInfo.already_added );
            const maskLayer = layersToAdd.find(layerInfo => layerInfo.id==='gaunagar-mask');
            if (maskLayer)
            {
                maskLayer.zIndex = 1000;
            }
            return layersToAdd.reverse().map( layerInfo => makeLayer(layerInfo)); // only add if already_added is false
        }
        catch (error)
        {
            console.error(error);
            showError('Layers', 'Failed to load layers. Please try reloading the page.', {duration: 0});
            return [];
        }
    },
    async removeLayers({state, commit},)
    {
        const map = getMap();
        const layerIds = state.guiLayersInfo.map(ctgry => ctgry.layers)
            .reduce((accumulated, currVal) => [...accumulated,...currVal], [])
            .map(layer => layer.id);
        layerIds.forEach(id =>
        {
            removeLayer(id, map);
        });
        commit('SET_GUI_LAYERS_INFO', []);
    },
    setLayerVisibleWithUI({commit}, {layer_id, visible})
    {
        getMapAsync().then(map =>
        {
            setLayerVisibleInMap(layer_id, visible, map);
            commit('SET_LAYER_PROPERTIES', {id: layer_id, properties: {visible} });
        });
    },
    async setLayerEnabledById({commit}, {layerId, enabled=true})
    {
        commit('SET_LAYER_PROPERTIES', {id: layerId, properties: {enabled}});
    },
    async updatePlanLayersFeatures(_, planId)
    {
        const {data: geojsons} = await fetchWithAuthToken(`api/plan/${planId}/geojsons/`);
        getMapAsync().then( map =>
        {
            Object.entries(geojsons).forEach(([layerId, geojson]) =>
            {
                const layer = getLayerById(layerId, map);
                console.assert(layer, `layer with id ${layerId} not found`);
                layer.getSource().clear();
                if (!geojson) return;
                layer.getSource().addFeatures(geojsonFormat.readFeatures(geojson));
            });
        });
    },
    clearPlanLayers()
    {
        getMapAsync().then( map =>
        {
            getInitialPlanLayerInfos().layers.forEach(({id: layerId}) =>
            {
                const layer = getLayerById(layerId, map);
                console.assert(layer, `layer with id ${layerId} not found`);
                layer && layer.getSource().clear();
            });
        });
    },
    updatePlanLayerVisibilityFor({getters}, {sst=false, bat=false, mhp=false})
    {
        console.assert(sst || bat || mhp, "None of the valid options is set for updating plan layer visibility");
        getters.getPlanLayerIds.forEach(layer_id =>
        {
            let visible = false;
            if (sst) 
                visible = layer_id==='plan_sst_clusters';
            else if (bat)
                visible = layer_id==='plan_bat_clusters'
            else if (mhp)
                visible = ["plan_mhp_intakes","plan_mhp_upstreams","discharge","plan_mhp_powerhouses","plan_mhp_aois", "streamline"].includes(layer_id)

            this.dispatch('layers/setLayerVisibleWithUI', {layer_id, visible}, {root: true});
        });
    },
};

const mutations = {
    SET_LAYER_INITIALIZED: (state, value) =>
    {
        state.layerIsInitialized = value;
    },
    SET_MUNICIPALITY_LAYER_ENABLED: (state, value) =>
    {
        state.municipalityLayerEnabled = value;
    },
    SET_GUI_LAYERS_INFO: (state, value) =>
    {
        state.guiLayersInfo = value;
    },
    SET_LAYER_PROPERTIES: (state, {id, properties}) =>
    {
        console.assert(properties instanceof Object);
        let found=false;
        for (let category of state.guiLayersInfo)
        {
            let layer = category.layers.find(layer => id===layer.id);
            if (layer)
            {
                // layer = {...layer, ...properties}; spreading like this doesn't work
                // because of reference value thing. Have to do it like done below
                Object.entries(properties).forEach( ([property,value]) =>
                    layer[property] = value
                );
                found = true;
                break;
            }
        }
        console.assert(found, `Layer with id: ${id} not found`)
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};

const getLegendURL = (style_name) =>
{
    return `http://geoserver.pathway.com.np/geoserver/wms?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&STRICT=false&style=${style_name}`;
}

const getInitialPlanLayerInfos = () => {
    const subGroups = {
        bat: {
            "title": "BAT",
            "order": 1
        },
        mhp: {
            "title": "MHP",
            "order": 2
        },
        sst: {
            "title": "SST",
            "order": 3
        },
        survey: {
            "title": "Survey",
            "order": 4
        },
    };

    return {
        "id": -1, // don't change this this is used in getPlanLayer id
        "title": "My Plan",
        "order": -1,
        "icon_url": '',
        "layers": [
            {
                "id": "plan_bat_clusters",
                "sub_group": subGroups.bat,
                "title": "BAT Clusters",
                "visible": false,
                "enabled": true,
                "source": "BAT Tool",
                "description": "BAT clusters in the selected plan",
                "legend_path": getLegendURL('MEP:bat_cluster'),
                "order": 1,
                "type": "Vector",
                "vector_params": {
                    "layer": "bat_polygon",
                    "url": null,
                    "style": feature  => getStyle({
                        geom: 'Polygon',
                        fill:{ color:'rgba(0,240,240,0.1)' },
                        stroke: {color: 'rgb(0,240,240)', width:3},
                        text: { text: `${feature.get('cluster_id')}`, },
                    })
                },
            },
            {
                "id": "discharge",
                "sub_group": subGroups.mhp,
                "title": "Discharge",
                "visible": false,
                "enabled": true,
                "zindex": -1,
                "source": "Pathway",
                "description": "Shows the locations of the streams whose discharge information is available in the system",
                "legend_path": getLegendURL('MEP:Discharge'), // in @js/tools/mhp.js
                "order": 1,
                "type": "WMS",
                "wms_params": {
                    "url":"http://geoserver.pathway.com.np/geoserver/MEP/wms",
                    "source_version":"1.1.1",
                    "source_layers":"MEP:dis_cms_wgs84",
                    "source_styles":null,
                    "layer":"dis_cms_wgs84"
                },
            },
            {
                "id": "streamline",
                "sub_group": subGroups.mhp,
                "title": "Streamline",
                "visible": false,
                "enabled": true,
                "source": "Pathway",
                "description": "Rivers of Nepal",
                "legend_path": getLegendURL('mhp_streamlines'), // in @js/tools/mhp.js
                "order": 1,
                "already_added": true,
            },
            {
                "id": "plan_mhp_intakes",
                "sub_group": subGroups.mhp,
                "title": "Intake",
                "visible": false,
                "enabled": true,
                "source": "MHP Tool",
                "description": "Intake pts",
                "legend_path": getLegendURL('MEP:mhp_intake'),
                "order": 2,
                "type": "Vector",
                "vector_params": {
                    "layer": "mhp_intake",
                    "url": null,
                    "style": {
                        geom: 'Point',
                        stroke: {color: '#0083f4'},
                        fill: {color: '#0083f4'},
                        radius: 5,
                        text: {text: 'Intake'}
                    },
                },
            },
            {
                "id": "plan_mhp_upstreams",
                "sub_group": subGroups.mhp,
                "title": "Upstream",
                "visible": false,
                "enabled": true,
                "source": "MHP Tool",
                "description": "Upstream streams",
                "legend_path": getLegendURL('MEP:mhp_upstream'),
                "order": 3,
                "type": "Vector",
                "vector_params": {
                    "layer": "mhp_upstream",
                    "url": null,
                    "style": {
                        geom: 'Line',
                        stroke: {color: '#0093d4', width: 2.8}
                    },
                },
            },
            {
                "id": "plan_mhp_powerhouses",
                "sub_group": subGroups.mhp,
                "title": "Powerhouses",
                "visible": false,
                "enabled": true,
                "source": "MHP Tool",
                "description": "Powerhouses",
                "legend_path": getLegendURL('MEP:mhp_powerhouses'),
                "order": 4,
                "type": "Vector",
                "vector_params": {
                    "layer": "mhp_powerhouses",
                    "url": null,
                    "style": feature =>
                    {
                        return getStyle({
                            geom: 'Point',
                            stroke: { color: '#009304',},
                            fill: { color: '#009304',},
                            radius: 5,
                            text: { text: `${parseFloat(feature.get('power_kW'))} kW`, }
                        });
                    },
                },
            },
            {
                "id": "plan_mhp_aois",
                "sub_group": subGroups.mhp,
                "title": "Area of Interests",
                "visible": false,
                "enabled": true,
                "source": "MHP Tool",
                "description": "Area of Interests",
                "legend_path": getLegendURL('MEP:mhp_aoi'),
                "order": 5,
                "type": "Vector",
                "vector_params": {
                    "layer": "mhp_aoi",
                    "url": null,
                    "style": {
                        geom: 'Polygon',
                        fill: {color: '#fff3'},
                        stroke: {color: '#0093d4', width: 2}
                    }
                },
            },
            {
                "id": "plan_sst_clusters",
                "sub_group": subGroups.sst,
                "title": "Sampling Clusters",
                "visible": false,
                "enabled": true,
                "source": "SST Tool",
                "description": "Sampling Clusters",
                "legend_path":  getLegendURL('MEP:sampling_cluster'),
                "order": 6,
                "type": "Vector",
                "vector_params": {
                    "layer": "sst_clusters",
                    "url": null,
                    style: feature => getStyle({
                        geom: 'Polygon',
                        fill:{ color:'rgba(225,225,0,0.1)' },
                        stroke: {color: '#fecd40', width:2},
                        text: {text: `${feature.get('psu_id')}`},
                    }),
                },
            },
            {
                "id": "plan_survey_household",
                "sub_group": subGroups.survey,
                "title": "Surveyed Household Locations",
                "visible": false,
                "enabled": true,
                "source": "ODK Survey",
                "description": "Location of the households that were surveyed",
                "legend_path": getLegendURL('MEP:surveyed_household'),
                "order": 1,
                "type": "Vector",
                "vector_params": {
                    "layer": "plan_survey_household",
                    "url": null,
                    "style": {
                        geom: 'Point',
                        stroke: {color: '#0083f4', width: 1},
                        fill: {color: '#0083f411'},
                        radius: 3,
                    },
                },
            },
        ],
    };
}
